import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  getSettings,
  updateSettings,
  uploadImageLogoClub,
} from "../../../../../services/settingsServices";

import { getAllGalleries } from "../../../../../services/gallerieServices";

import {
  buildParametresStats,
  DEFAULT_SETTINGS,
  isDemoUser,
  normalizeSettingsPayload,
  safeJsonParse,
} from "../utils/parametresUtils";

export default function useParametres() {
  const [activeTab, setActiveTab] = useState("identity");

  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [initialSettings, setInitialSettings] = useState(DEFAULT_SETTINGS);
  const [galleries, setGalleries] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [isDemoMode, setIsDemoMode] = useState(false);

  const loadUserMode = useCallback(() => {
    const user =
      safeJsonParse(localStorage.getItem("user"), null) ||
      safeJsonParse(localStorage.getItem("userInfo"), null);

    setIsDemoMode(isDemoUser(user));
  }, []);

  const refreshData = useCallback(async () => {
    setLoading(true);

    try {
      const [settingsData, galleriesData] = await Promise.all([
        getSettings(),
        getAllGalleries(),
      ]);

      const firstSettings = Array.isArray(settingsData)
        ? settingsData[0]
        : settingsData;

      const nextSettings = {
        ...DEFAULT_SETTINGS,
        ...(firstSettings || {}),
      };

      setSettings(nextSettings);
      setInitialSettings(nextSettings);
      setGalleries(Array.isArray(galleriesData) ? galleriesData : []);
    } catch (error) {
      console.error("Erreur lors de la récupération des paramètres :", error);
      toast.error("Erreur lors de la récupération des paramètres.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUserMode();
    refreshData();
  }, [loadUserMode, refreshData]);

  const guardDemoMode = useCallback(() => {
    if (!isDemoMode) return false;

    toast.info("Fonction désactivée en mode démonstration.");
    return true;
  }, [isDemoMode]);

  const handleChange = useCallback((event) => {
    const { name, value } = event.target;

    setSettings((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleQuillChange = useCallback((name, value) => {
    setSettings((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleLogoChange = useCallback(
    async (event) => {
      if (guardDemoMode()) return;

      const file = event.target.files?.[0];
      if (!file) return;

      const formData = new FormData();
      formData.append("image", file);
      formData.append("category", "settings");

      setUploadingLogo(true);

      try {
        const uploadResponse = await uploadImageLogoClub(formData);

        const uploadedUrl =
          uploadResponse?.filePath ||
          uploadResponse?.imageUrl ||
          uploadResponse?.url;

        if (!uploadedUrl) {
          throw new Error("La réponse de l'API ne contient pas de chemin image.");
        }

        setSettings((prev) => ({
          ...prev,
          logo_club: uploadedUrl,
        }));

        toast.success(
          "Logo téléversé. Pensez à enregistrer les paramètres."
        );
      } catch (error) {
        console.error("Erreur lors de l'upload du logo :", error);
        toast.error("Erreur lors de l'upload du logo.");
      } finally {
        setUploadingLogo(false);
        event.target.value = "";
      }
    },
    [guardDemoMode]
  );

  const handleSubmit = useCallback(
    async (event) => {
      event?.preventDefault?.();

      if (guardDemoMode()) return;

      setSaving(true);

      try {
        const payload = normalizeSettingsPayload(settings);

        await updateSettings(payload);

        setInitialSettings(payload);
        setSettings(payload);

        toast.success("Paramètres du club mis à jour avec succès.");
      } catch (error) {
        console.error(
          "Erreur lors de la mise à jour des paramètres du club :",
          error
        );
        toast.error("Erreur lors de la mise à jour des paramètres.");
      } finally {
        setSaving(false);
      }
    },
    [guardDemoMode, settings]
  );

  const resetChanges = useCallback(() => {
    setSettings(initialSettings);
    toast.info("Modifications réinitialisées.");
  }, [initialSettings]);

  const hasUnsavedChanges = useMemo(() => {
    return JSON.stringify(settings) !== JSON.stringify(initialSettings);
  }, [initialSettings, settings]);

  const stats = useMemo(
    () => buildParametresStats(settings, galleries),
    [settings, galleries]
  );

  return {
    activeTab,
    setActiveTab,
    settings,
    galleries,
    loading,
    saving,
    uploadingLogo,
    hasUnsavedChanges,
    stats,
    actions: {
      refreshData,
      handleChange,
      handleQuillChange,
      handleLogoChange,
      handleSubmit,
      resetChanges,
    },
  };
}